"use client";

import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { webSiteNavigationLabels } from "../webSiteNavigation";
import { useWebMobileMenuOverlay } from "./useWebMobileMenuOverlay";

export function TorqueServiceMobileMenu({
    languageCode,
}: {
    languageCode?: string | null;
}) {
    const navigationId = useId();
    const sentinelRef = useRef<HTMLSpanElement>(null);
    const toggleRef = useRef<HTMLButtonElement>(null);
    const [header, setHeader] = useState<HTMLElement | null>(null);
    const [isOpen, setIsOpen] = useState(false);
    const labels = webSiteNavigationLabels(languageCode);

    useEffect(() => {
        const template = sentinelRef.current?.closest<HTMLElement>(
            '[data-web-template="torque-service"]',
        ) ?? null;
        const nextHeader = template?.querySelector<HTMLElement>(
            '[data-web-region="header"]',
        ) ?? null;
        const ownerDocument = template?.ownerDocument;
        const view = ownerDocument?.defaultView;

        setHeader(nextHeader);

        if (!template || !nextHeader || !ownerDocument || !view) {
            return;
        }

        const reducedMotion = view.matchMedia(
            "(prefers-reduced-motion: reduce)",
        ).matches;
        const revealSelector = [
            '[data-web-builder-region="page"] .site-builder-section',
        ].join(",");
        let animationFrame: number | null = null;

        const updateHeader = () => {
            animationFrame = null;
            nextHeader.toggleAttribute(
                "data-torque-header-scrolled",
                view.scrollY > 12,
            );
        };
        const requestHeaderUpdate = () => {
            if (animationFrame === null) {
                animationFrame = view.requestAnimationFrame(updateHeader);
            }
        };

        updateHeader();
        view.addEventListener("scroll", requestHeaderUpdate, { passive: true });

        if (template.dataset.webTemplateEditor === "true") {
            return () => {
                view.removeEventListener("scroll", requestHeaderUpdate);
                if (animationFrame !== null) {
                    view.cancelAnimationFrame(animationFrame);
                }
                nextHeader.removeAttribute("data-torque-header-scrolled");
            };
        }

        const revealObserver = !reducedMotion && "IntersectionObserver" in view
            ? new view.IntersectionObserver((entries, observer) => {
                for (const entry of entries) {
                    if (!entry.isIntersecting) {
                        continue;
                    }

                    entry.target.setAttribute("data-torque-revealed", "true");
                    observer.unobserve(entry.target);
                }
            }, {
                rootMargin: "0px 0px -8% 0px",
                threshold: 0.08,
            })
            : null;
        const registered = new Set<HTMLElement>();
        const registerReveal = (root: ParentNode) => {
            const candidates = [
                ...(root instanceof view.HTMLElement && root.matches(revealSelector)
                    ? [root]
                    : []),
                ...Array.from(root.querySelectorAll<HTMLElement>(revealSelector)),
            ];

            for (const element of candidates) {
                if (
                    registered.has(element)
                    || element.dataset.webRegion === "mobileAction"
                ) {
                    continue;
                }

                registered.add(element);
                element.setAttribute("data-torque-reveal", "true");

                if (revealObserver) {
                    revealObserver.observe(element);
                } else {
                    element.setAttribute("data-torque-revealed", "true");
                }
            }
        };

        registerReveal(template);
        template.setAttribute("data-torque-runtime-ready", "true");

        const mutationObserver = new view.MutationObserver((entries) => {
            for (const entry of entries) {
                for (const node of entry.addedNodes) {
                    if (node instanceof view.HTMLElement) {
                        registerReveal(node);
                    }
                }
            }
        });

        mutationObserver.observe(template, { childList: true, subtree: true });

        return () => {
            view.removeEventListener("scroll", requestHeaderUpdate);
            revealObserver?.disconnect();
            mutationObserver.disconnect();
            if (animationFrame !== null) {
                view.cancelAnimationFrame(animationFrame);
            }
            nextHeader.removeAttribute("data-torque-header-scrolled");
            template.removeAttribute("data-torque-runtime-ready");
            for (const element of registered) {
                element.removeAttribute("data-torque-reveal");
                element.removeAttribute("data-torque-revealed");
            }
        };
    }, []);

    useWebMobileMenuOverlay({
        header,
        isOpen,
        navigationId,
        openAttribute: "data-torque-mobile-menu-open",
        setIsOpen,
        templateCode: "torque-service",
        toggleRef,
    });

    return (
        <>
            <span
                aria-hidden="true"
                data-torque-mobile-menu-runtime="true"
                ref={sentinelRef}
                style={{ display: "none" }}
            />
            {header
                ? createPortal(
                    <button
                        aria-controls={navigationId}
                        aria-expanded={isOpen}
                        aria-label={isOpen ? labels.closeMenu : labels.openMenu}
                        className="torque-mobile-menu-toggle"
                        onClick={(event) => {
                            event.stopPropagation();
                            setIsOpen((current) => !current);
                        }}
                        onPointerDown={(event) => event.stopPropagation()}
                        ref={toggleRef}
                        type="button"
                    >
                        <span aria-hidden="true" />
                        <span aria-hidden="true" />
                        <span aria-hidden="true" />
                    </button>,
                    header,
                )
                : null}
        </>
    );
}
